import { useMutation } from '@tanstack/react-query';
import { getOrderStatusReconciliationJobResult } from 'config/requests/reconciliation';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useNotify } from 'react-admin';

import { useMarkNotificationDownloaded } from './useMarkNotificationDownloaded';

type Props = {
  notificationId: string;
  jobId: number;
  fileExtension?: string;
  fileUrl?: string;
  onSuccess?: () => void;
};

type ReturnValue = {
  downloadResult: VoidFunction;
  isPending: boolean;
};

export function useDownloadReconciliationResult({
  notificationId,
  jobId,
  fileExtension,
  fileUrl,
  onSuccess,
}: Props): ReturnValue {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const { markNotificationDownloaded } = useMarkNotificationDownloaded({ onSuccess });

  const { mutate, isPending } = useMutation({
    mutationFn: getOrderStatusReconciliationJobResult,
    onSuccess: (file) => {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-status-reconciliation-${jobId}.${fileExtension}`;
      link.click();
      URL.revokeObjectURL(url);

      markNotificationDownloaded(notificationId);
    },
    onError: () => {
      notify(translate('app.common.fileDownloadFailure'), { type: 'error' });
    },
  });

  const downloadResult = () => {
    if (fileUrl) {
      mutate(fileUrl);
    }
  };

  return { downloadResult, isPending };
}
